/**
 * Tijd in woorden voor het midden. Momenten reizen als UTC; JP leest
 * Amsterdamse kloktijd.
 */
import { useEffect, useState } from "react";

const AMS = "Europe/Amsterdam";

/** "14:02" in Amsterdamse tijd. */
export function klokje(iso: string | number | Date): string {
  return new Date(iso).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: AMS,
  });
}

/** "wacht 2 u 40", "wacht 12 min", "wacht 18 u", "wacht 3 d". Leeg als er niets te wachten is. */
export function wachtWoord(sinds: string | null, nu: number): string {
  if (!sinds) return "";
  const minuten = Math.floor((nu - new Date(sinds).getTime()) / 60_000);
  if (!Number.isFinite(minuten) || minuten < 1) return "";
  if (minuten < 60) return `wacht ${minuten} min`;
  const uren = Math.floor(minuten / 60);
  if (uren < 10) return minuten % 60 ? `wacht ${uren} u ${minuten % 60}` : `wacht ${uren} u`;
  if (uren < 48) return `wacht ${uren} u`;
  return `wacht ${Math.floor(uren / 24)} d`;
}

/** Duur van spraak of voicemail: "0:19", "2:05". */
export function duurWoord(seconden: number | null): string {
  if (seconden === null || !Number.isFinite(seconden)) return "";
  const s = Math.max(0, Math.round(seconden));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Is het in Amsterdam al 17:00 of later? Dan is "vandaag 17:00" geen keuze meer. */
/** Vanaf 16:50: "Vandaag 17:00" zou dan geen gepland bericht meer zijn (sessie 75). */
export function naVijf(nu: number): boolean {
  const [uur, minuut] = new Date(nu)
    .toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: AMS })
    .split(":")
    .map(Number);
  return uur > 16 || (uur === 16 && minuut >= 50);
}

/** Een klok die elke `ms` tikt, zodat wachttijd en ongedaan-vensters meelopen. */
export function useNu(ms: number, actief = true): number {
  const [nu, setNu] = useState(() => Date.now());
  useEffect(() => {
    if (!actief) return;
    const timer = setInterval(() => setNu(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms, actief]);
  return nu;
}

/** "wo 14 okt 12:24" in Amsterdamse tijd (kop en klantmail, mock v10). */
export function dagKlok(iso: string | number | Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dag = d
    .toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short", timeZone: AMS })
    .replace(/\./g, "")
    .replace(",", "");
  return `${dag} ${klokje(d)}`;
}
